import React from "react";
import { AxisBottom, AxisLeft, ComputedTick } from '@visx/axis';
import { Group } from "@visx/group";
import { Line } from "@visx/shape";
import moment from "moment";
import { scaleBand, scaleTime } from "@visx/scale";
import { Theme, Tooltip, withStyles } from "@material-ui/core";
import { BaseColor, Line2019Color, Line2020Color } from "../consts";

const tickLabelSize = 15;
const BigTooltip = withStyles((theme: Theme) => ({
    tooltip: {
        fontSize: "0.8em",
    },
}))(Tooltip);

export function TwoYearAxis(graphHeight: number, leftPad: number, graphWidth: number, xScale: any, yScale: any, yAxisLabel: string) {
    return (
        <>
            <AxisBottom top={graphHeight} scale={xScale} numTicks={23} tickFormat={(d: any) => { return d }}>
                {props => {
                    const axisCenter = (props.axisToPoint.x - props.axisFromPoint.x) / 2;
                    return (
                        <g className="my-custom-bottom-axis">
                            <Line from={props.axisFromPoint} to={props.axisToPoint} stroke={"#000000"} />
                            {props.ticks.map((tick, i) => {
                                if (i !== 24) {
                                    const tickX = tick.to.x;
                                    const tickY = tick.to.y + tickLabelSize;
                                    return (
                                        <Group key={`vx-tick-${tick.value}-${i}`} className={'vx-axis-tick'}>
                                            <Line from={tick.from} to={tick.to} stroke={"#000000"} />
                                            <text transform={`translate(${tickX}, ${tickY})`} fontSize={tickLabelSize} textAnchor="middle" fill={moment(tick.formattedValue).format("Y") === "2019" ? '#4760DE' : "#A62190"} >
                                                {moment(tick.formattedValue).format("MMM")}
                                            </text>
                                        </Group>
                                    );
                                }
                            })}
                            <text textAnchor="middle" transform={`translate(${axisCenter}, 50)`} fontSize="8" >
                                {props.label}
                            </text>
                        </g>
                    );
                }}
            </AxisBottom>
            <AxisBottom top={graphHeight + 30} scale={scaleBand({ domain: ["2019", "2020"], range: [leftPad, graphWidth] })} numTicks={4} hideAxisLine tickFormat={(d: any) => { return moment(d).format("Y") }} >
                {props => {
                    const tickLabelSize = 10;
                    const axisCenter = (props.axisToPoint.x - props.axisFromPoint.x) / 2;
                    return (
                        <g className="my-custom-bottom-axis">
                            {props.ticks.map((tick, i) => {
                                const tickX = tick.to.x;
                                const tickY = tick.to.y + tickLabelSize;
                                return (
                                    <Group key={`vx-tick-${tick.value}-${i}`} className={'vx-axis-tick'}>
                                        <text transform={`translate(${tickX}, ${tickY})`} fontSize={tickLabelSize} textAnchor="middle" fill={tick.formattedValue === "2019" ? Line2019Color : Line2020Color} >
                                            {tick.formattedValue}
                                        </text>
                                    </Group>
                                );
                            })}
                            <text textAnchor="middle" transform={`translate(${axisCenter}, 50)`} fontSize="8" >
                                {props.label}
                            </text>
                        </g>
                    );
                }}
            </AxisBottom>

            <AxisLeft left={leftPad} scale={yScale} label={yAxisLabel} labelClassName={"axisLabelTY"} />
        </>
    )
}

export function OneYearAxis(graphHeight: number, leftPad: number, graphWidth: number, xScale: any, yScale: any, yAxisLabel: string) {
    return (
        <>
            <AxisBottom top={graphHeight} scale={xScale} numTicks={12} tickFormat={(d: any) => { return d }} >
                {props => {
                    const axisCenter = (props.axisToPoint.x - props.axisFromPoint.x) / 2;
                    return (
                        <g className="my-custom-bottom-axis">
                            <Line from={props.axisFromPoint} to={props.axisToPoint} stroke={"#000000"} />
                            {props.ticks.map((tick, i) => {
                                return monthTicks(tick)
                            })}
                            <text textAnchor="middle" transform={`translate(${axisCenter}, 50)`} fontSize="8" >
                                {props.label}
                            </text>
                        </g>
                    );
                }}
            </AxisBottom>
            <AxisBottom top={graphHeight + 30} scale={scaleTime<number>({ domain: [new Date("2019-01-01"), new Date("2020-12-01")], nice: true, range: [leftPad, graphWidth] })} numTicks={1} hideAxisLine tickFormat={(d: any) => { return moment(d).format("Y") }} hideTicks />

            <AxisLeft left={leftPad} scale={yScale} label={yAxisLabel} labelClassName={"axisLabelOY"} />
        </>
    )
}

function monthTicks(tick: ComputedTick<any>) {
    let tooltipText = "Prior to Lockdown"
    let tickColor = BaseColor

    let currMonth = moment(tick.formattedValue).format("MMMM")
    if (["April", "May"].includes(currMonth)) {
        tooltipText = "Lockdown 1"
        tickColor = Line2019Color
    }
    else if (["July", "August", "September", "October"].includes(currMonth)) {
        tooltipText = "Lockdown 2"
        tickColor = Line2020Color
    }
    else if (["January", "February"].includes(currMonth)) {
        tooltipText = "Prior to Lockdown"
        tickColor = BaseColor
    }
    else if (currMonth === "March") {
        tooltipText = "Lockdown begins in Third Week"
        tickColor = Line2019Color
    }
    else if (currMonth === "June") {
        tooltipText = "Eased Restrictions"
        tickColor = BaseColor
    }
    else if (["November", "December"].includes(currMonth)) {
        tooltipText = "Easing Restrictions Over Time"
        tickColor = BaseColor
    }

    return (
        <Group key={`vx-tick-${tick.value}`}>
            <Line from={tick.from} to={tick.to} stroke={"#000000"} />
            <BigTooltip title={tooltipText} className={"largerTooltip"}>
                <text transform={`translate(${tick.to.x}, ${tick.to.y + tickLabelSize})`} fontSize={tickLabelSize} textAnchor="middle" fill={tickColor} cursor={'pointer'}>
                    {moment(tick.formattedValue).format("MMMM")}
                </text>
            </BigTooltip>
        </Group>
    );
}